import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { toApiError, useApi } from '@/shared/api';
import {
  captureRemovalRequest,
  confirmRemovalInCache,
  refreshRemovalQueries,
  type RemovalRequest,
  type RemovalTarget,
} from './revisionRemovalCache';

export function useRevisionRemoval() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useTranslation('revisionHolds');
  const [selected, setSelected] = useState<RemovalTarget | null>(null);
  const [error, setError] = useState<string | null>(null);
  const removal = useMutation({
    mutationFn: (request: RemovalRequest) => api.channex.removeQuarantinedRevision(request.id),
    networkMode: 'always',
    onSuccess: async (result, request) => {
      await confirmRemovalInCache(queryClient, request, result.removedAt);
      setSelected(null);
      toast.success(t('removed'));
      await refreshRemovalQueries(queryClient, request);
    },
    onError: async (failure, request) => {
      const problem = await toApiError(failure);
      setError(problem.status === 0 ? t('removeUnknown') : problem.detail || t('removeFailed'));
      await refreshRemovalQueries(queryClient, request);
    },
  });
  return {
    target: selected?.id ?? null,
    error,
    pending: removal.isPending,
    open: (row: RemovalTarget) => {
      setError(null);
      setSelected(Object.freeze({ id: row.id, propertyGroupId: row.propertyGroupId }));
    },
    close: () => {
      if (!removal.isPending) setSelected(null);
    },
    confirm: () => {
      if (selected) removal.mutate(captureRemovalRequest(queryClient, selected));
    },
  };
}
